import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setIsMenuOpen from "@store/Store/Pages/MyAccount/Handlers/SetIsMenuOpen";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import isWebsiteAccessedByMobileApp from "@store/utilities/isWebsiteAccessedByMobileApp";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    hideForMobile = "hideForMobile",
    hideForMobileApp = "hideForMobileApp",
    isOverlay = "isOverlay",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.hideForMobile]: boolean;
        [fields.hideForMobileApp]: boolean;
        [fields.isOverlay]: boolean;
    };
}

const mapStateToProps = (state: StoreApplicationState) => ({
    isMenuOpen: state.store.pages.myAccount.isMenuOpen,
});

const mapDispatchToProps = {
    setIsMenuOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface SideColumnRowStyles {
    wrapper?: InjectableCss;
    sideBar?: InjectableCss;
    mainContent?: InjectableCss;
}

const SideColumnRowStyles: SideColumnRowStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
        `
    },
    sideBar: {
        css: css`
            margin-right: 1em;
        `
    },
    mainContent: {
        css: css`
            flex: 1;
        `
    },
}

const styles = SideColumnRowStyles;

const SideColumnRow: React.FC<Props> = (props: Props) => {
    const {
        fields: { hideForMobile, hideForMobileApp, isOverlay },
        id,
        isMenuOpen,
        setIsMenuOpen,
    } = props;

    const [menuWidth, setMenuWidth] = useState<number>(0);

    useEffect(() => {
        isOverlay ? setIsMenuOpen(false) : setIsMenuOpen(true);
    }, []);

    useEffect(() => {
        // Push content when menu is active if not in overlay
        !isOverlay && isMenuOpen ? setMenuWidth(370) : setMenuWidth(50);
    }, [isMenuOpen]);

    const isHiddenForMobileApp = hideForMobileApp && isWebsiteAccessedByMobileApp();

    const ConditionalMobileWrapper: React.FC = ({ children }) => {
        return (
            <>
              {hideForMobile ? <Hidden below="md">{children}</Hidden> : children}
            </>
          );
    };

    return (
        <StyledWrapper {...styles.wrapper}>
                {!isHiddenForMobileApp && (
                    <ConditionalMobileWrapper>
                        <StyledWrapper {...styles.sideBar} style={{ width: `${menuWidth}px` }}>
                            <Zone contentId={id} zoneName="Content0" />
                        </StyledWrapper>
                    </ConditionalMobileWrapper>
                )}
            <StyledWrapper {...styles.mainContent}>
                <Zone contentId={id} zoneName="Content1" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SideColumnRow),
    definition: {
        group: "Basic",
        icon: "Columns",
        fieldDefinitions: [
            {
                name: fields.hideForMobile,
                displayName: "Hide side column for smaller devices",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.hideForMobileApp,
                displayName: "Hide side column for mobile app",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.isOverlay,
                displayName: "Set menu in overlay",
                tooltip: "If enable, the side menu will render on top of content in a popover.",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
